import { useMemo, useRef } from 'react';
import type { TeamMetrics } from './useTeamSeasonMetrics';

export type InsightLevel = 'informational' | 'contextual' | 'strong';
export type StatFamily = 'sot' | 'corners' | 'shots';

export interface LiveInsight {
  id: string; // e.g. "home|sot|4"
  team: 'home' | 'away';
  teamName: string;
  statFamily: StatFamily;
  currentCount: number;
  ratio: number | null; // e.g. 3.27 SOT per goal
  level: InsightLevel;
  message: string;
  subMessage: string | null;
  strongMessage: string | null;
}

const STAT_LABELS: Record<StatFamily, { singular: string; plural: string; ratioLabel: string }> = {
  sot: { singular: 'shot on target', plural: 'shots on target', ratioLabel: 'shots on target' },
  corners: { singular: 'corner', plural: 'corners', ratioLabel: 'corners' },
  shots: { singular: 'shot', plural: 'shots', ratioLabel: 'shots' },
};

const MIN_GAMES = 5;
const MIN_GOALS = 3;

function hasValidRatio(metrics: TeamMetrics | null): boolean {
  return !!metrics && metrics.games_played >= MIN_GAMES && metrics.goals_scored >= MIN_GOALS;
}

function getRatio(metrics: TeamMetrics | null, family: StatFamily): number | null {
  if (!hasValidRatio(metrics)) return null;
  switch (family) {
    case 'sot': return metrics!.sot_per_goal;
    case 'corners': return metrics!.corners_per_goal;
    case 'shots': return metrics!.shots_per_goal;
  }
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function buildInsight(
  team: 'home' | 'away',
  teamName: string,
  family: StatFamily,
  count: number,
  metrics: TeamMetrics | null,
): LiveInsight | null {
  if (count <= 0) return null;

  const labels = STAT_LABELS[family];
  const ratio = getRatio(metrics, family);
  const statWord = count === 1 ? labels.singular : labels.plural;

  // Primary message: always the count
  const message = `This was ${teamName}'s ${ordinal(count)} ${statWord} in the match.`;

  let level: InsightLevel = 'informational';
  let subMessage: string | null = null;
  let strongMessage: string | null = null;

  if (ratio !== null) {
    subMessage = `They average 1 goal every ${ratio.toFixed(2)} ${labels.ratioLabel}.`;
    
    if (count >= ratio) {
      level = 'strong';
      strongMessage = 'Above their usual scoring average in a 0-0.';
    } else {
      level = 'contextual';
    }
  }

  return {
    id: `${team}|${family}|${count}`,
    team,
    teamName,
    statFamily: family,
    currentCount: count,
    ratio,
    level,
    message,
    subMessage,
    strongMessage,
  };
}

interface LiveStats {
  homeSot: number;
  awaySot: number;
  homeCorners: number;
  awayCorners: number;
  homeShots: number;
  awayShots: number;
}

interface UseLiveInsightsOptions {
  homeTeamName: string;
  awayTeamName: string;
  homeMetrics: TeamMetrics | null;
  awayMetrics: TeamMetrics | null;
  homeScore: number;
  awayScore: number;
  isLive: boolean;
  stats: LiveStats | null;
}

/**
 * Core insight engine: compares live match stats against historical team ratios.
 * Only active when score is 0-0.
 * Returns max 4 insights (2 per team, most impactful stat family).
 */
export function useLiveInsights({
  homeTeamName,
  awayTeamName,
  homeMetrics,
  awayMetrics,
  homeScore,
  awayScore,
  isLive,
  stats,
}: UseLiveInsightsOptions): LiveInsight[] {
  const shownRef = useRef(new Set<string>());

  return useMemo(() => {
    // Gate: only 0-0 live
    if (!isLive || homeScore !== 0 || awayScore !== 0 || !stats) {
      return [];
    }

    const families: StatFamily[] = ['sot', 'corners', 'shots'];
    const allInsights: LiveInsight[] = [];

    for (const family of families) {
      const homeCount = family === 'sot' ? stats.homeSot : family === 'corners' ? stats.homeCorners : stats.homeShots;
      const awayCount = family === 'sot' ? stats.awaySot : family === 'corners' ? stats.awayCorners : stats.awayShots;

      const homeInsight = buildInsight('home', homeTeamName, family, homeCount, homeMetrics);
      const awayInsight = buildInsight('away', awayTeamName, family, awayCount, awayMetrics);

      if (homeInsight) allInsights.push(homeInsight);
      if (awayInsight) allInsights.push(awayInsight);
    }

    // Sort: strong first, then contextual, then informational
    const levelOrder: Record<InsightLevel, number> = { strong: 0, contextual: 1, informational: 2 };
    allInsights.sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);

    // Limit: max 2 per team
    const homeInsights = allInsights.filter(i => i.team === 'home').slice(0, 2);
    const awayInsights = allInsights.filter(i => i.team === 'away').slice(0, 2);

    return [...homeInsights, ...awayInsights];
  }, [isLive, homeScore, awayScore, stats, homeMetrics, awayMetrics, homeTeamName, awayTeamName]);
}
